import { Building2, Key, Activity } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ExpandableTile, StatRow } from '../../components/dashboard'

const PERCENT_MAX = 100

interface PlatformStats {
  organisations: number
  licenses: number
  activeLicenses: number
}

function OrganisationsTile({ stats }: { stats: PlatformStats }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <ExpandableTile
      title={t('stats.organisations')}
      value={stats.organisations}
      icon={Building2}
      viewAllLink="/admin/organisations"
    >
      <div className="space-y-1">
        <p
          className="label-mono text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.quickStats')}
        </p>
        <StatRow label={t('stats.totalOrganisations')} value={stats.organisations} highlight />
        <StatRow label={t('stats.withActiveLicenses')} value={stats.activeLicenses} />
      </div>
    </ExpandableTile>
  )
}

function ActiveLicensesTile({ stats }: { stats: PlatformStats }): React.ReactElement {
  const { t } = useTranslation('admin')
  const utilisation =
    stats.licenses > 0
      ? `${String(Math.round((stats.activeLicenses / stats.licenses) * PERCENT_MAX))}%`
      : '-'
  return (
    <ExpandableTile
      title={t('stats.activeLicenses')}
      value={stats.activeLicenses}
      icon={Key}
      viewAllLink="/admin/licenses"
    >
      <div className="space-y-1">
        <p
          className="label-mono text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.licenseStatus')}
        </p>
        <StatRow label={t('stats.active')} value={stats.activeLicenses} highlight />
        <StatRow label={t('stats.totalLicenses')} value={stats.licenses} />
        <StatRow label={t('stats.utilisation')} value={utilisation} />
      </div>
    </ExpandableTile>
  )
}

function TotalLicensesTile({ stats }: { stats: PlatformStats }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <ExpandableTile
      title={t('stats.totalLicenses')}
      value={stats.licenses}
      icon={Activity}
      viewAllLink="/admin/licenses"
    >
      <div className="space-y-1">
        <p
          className="label-mono text-xs font-medium tracking-wider uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('stats.allLicenses')}
        </p>
        <StatRow label={t('stats.active')} value={stats.activeLicenses} highlight />
        <StatRow label={t('stats.inactive')} value={stats.licenses - stats.activeLicenses} />
      </div>
    </ExpandableTile>
  )
}

export function PlatformTiles({ stats }: { stats: PlatformStats }): React.ReactElement {
  return (
    <>
      <OrganisationsTile stats={stats} />
      <ActiveLicensesTile stats={stats} />
      <TotalLicensesTile stats={stats} />
    </>
  )
}
